import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactFloatingInput from '@React_UI_Lib/ReactFloatingInput';
import { getAppEnv } from '../../../../../config/env';
import SearchSuggestionList from '@React_UI_Lib/SearchSuggestionList';
import './LocationSearch.css';
import {
  CityData,
  getSuggestions
} from '../../../../utils/cabAutoCompleteUtils';
import debounce from 'lodash/debounce';
import { SEO_CABS_LANDING_PAGE } from '../../../../constants/templateConstants';
import { CityLocation } from '../../../runtime-typechecker';
import {
  getAirportLocationsFromLs,
  getCityLocationsFromLs
} from '../../../../utils/localStorageUtils';
import { isSSR } from '../../../../utils/commonUtils';
import {
  CabsLandingContext,
  CabsLandingDispatchContext
} from '../../../../CabsLandingContext';
import {
  SET_SOURCE_CITY,
  SET_DESTINATION_CITY,
  SET_WIDGET,
  SET_RENTAL_CITY,
  SET_AIRPORT,
  SET_AIRPORT_CITY,
  SET_ACTIVE_STOPOVER,
  SET_STOPOVERS
} from '../../cabsLandingPageState';
import {
  CAB_FROM_AIRPORT,
  CAB_TO_AIRPORT,
  RENTALS,
  isAirportCheck,
  isOutstationCheck,
  OUTSTATION_ONE_WAY,
  OUTSTATION_ROUND_TRIP
} from '../../../../constants/tripTypes';
import { getPlaceObject } from '../../../../utils/reverseGeoCode';
import { cabsLandingWidgetInfo } from '../../../../cabsLandingWidgetInfo';
import { SwapIcon } from '../SwapIcon/SwapIcon';
import {
  MOB_CABS_AT_LANDING_DESTINATION_SELECT,
  MOB_CABS_AT_LANDING_PICKUP_SELECT,
  MOB_CABS_HR_LANDING_PICKUP_SELECT,
  MOB_CABS_OW_LANDING_DESTINATION_SELECT,
  MOB_CABS_OW_LANDING_PICKUP_SELECT,
  MOB_CABS_RT_LANDING_DESTINATION_SELECT,
  MOB_CABS_RT_LANDING_PICKUP_SELECT
} from '../../../../constants/omnitureConstants';
const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

const SearchPage = ({
  fromLabel = 'FROM',
  toLabel = 'TO',
  focusFrom = true,
  focusTo = false,
  sourceLocation,
  destinationLocation,
  onFromChange,
  onToChange,
  deactivateStopOver,
  fromPlaceHolder = 'Enter Pick up location',
  toPlaceHolder = 'Enter Drop location'
}: {
  fromLabel?: string;
  toLabel?: string;
  focusFrom?: boolean;
  focusTo?: boolean;
  sourceLocation;
  destinationLocation;
  onFromChange;
  onToChange;
  deactivateStopOver?: () => void;
  fromPlaceHolder?;
  toPlaceHolder?;
}) => {
  const cabsLandingContext = useContext(CabsLandingContext);
  const dispatch = useContext(CabsLandingDispatchContext);

  const {
    travelFor,
    activeStopOver,
    widget,
    stopOvers,
    trackTemplateEvent
  } = cabsLandingContext;

  const stopOversPresentInDisplay =
    stopOvers.length > 0 && isOutstationCheck(travelFor);

  const closeSection = () => {
    dispatch && dispatch({ type: SET_WIDGET, newStateValue: null });
  };

  const getRecentSearchesResult = () => {
    if (isSSR()) return [];

    if (
      (travelFor === CAB_TO_AIRPORT && toSelected) ||
      (travelFor == CAB_FROM_AIRPORT && fromSelected)
    )
      return getAirportLocationsFromLs();

    return getCityLocationsFromLs();
  };

  const [fromInputValue, setFromInputValue] = useState<string | null>(null);
  const [toInputValue, setToInputValue] = useState<string | null>(null);
  const fromInputRef = useRef<any>(null);
  const toInputRef = useRef<any>(null);
  const [fromSelected, setFromSelected] = useState(focusFrom ? true : false);
  const [toSelected, setToSelected] = useState(focusTo ? true : false);
  const [suggestions, setSuggestions] = useState<undefined | CityData[]>([]);
  const [recentSearches, setRecentSearches] = useState<undefined | CityData[]>(
    getRecentSearchesResult()
  );
  const [locationUpdateOrigin, setLocationUpdateOrigin] = useState<
    string | null
  >(null);
  const [componentDidMount, setComponentDidMount] = useState<boolean>(false);

  const selectedWidget = widgetNumber => {
    switch (true) {
      case travelFor === CAB_FROM_AIRPORT:
        return fromSelected
          ? cabsLandingWidgetInfo.FROM_AIRPORT_LOCATION_WIDGET
          : cabsLandingWidgetInfo.FROM_AIRPORT_CITY_LOCATION_WIDGET;

      case travelFor === CAB_TO_AIRPORT:
        return fromSelected
          ? cabsLandingWidgetInfo.TO_AIRPORT_CITY_LOCATION_WIDGET
          : cabsLandingWidgetInfo.TO_AIRPORT_LOCATION_WIDGET;

      default:
        return widgetNumber;
    }
  };
  const onSuggestionsFetchRequested = debounce(async ({ value }) => {
    if (!value) {
      setSuggestions([]);
      return;
    }
    const suggestionsFetched = await getSuggestions(
      value,
      travelFor,
      selectedWidget(widget),
      SEO_CABS_LANDING_PAGE
    );
    if (
      Array.isArray(suggestionsFetched) &&
      suggestionsFetched[0]?.citieslist?.length > 0
    )
      setSuggestions(suggestionsFetched[0]?.citieslist);
  }, 300);

  const closeWidgetOnLocationUpdate = locationUpdateOrigin => {
    return (
      locationUpdateOrigin &&
      ['RECENT_SEARCHES', 'SUGGESTIONS'].includes(locationUpdateOrigin)
    );
  };

  // Auto suggestions effects
  useEffect(() => {
    onSuggestionsFetchRequested({ value: fromInputValue || toInputValue });
  }, [fromInputValue, toInputValue, fromSelected, toSelected]);

  // recent searches effects
  useEffect(() => {
    setRecentSearches(getRecentSearchesResult());
  }, [fromSelected, toSelected]);

  // didMount useEffects
  useEffect(() => {
    setTimeout(() => {
      if (focusFrom) fromInputRef?.current?.focus();
      else if (focusTo) toInputRef?.current?.focus();
    }, 0);
    setComponentDidMount(true);
  }, []);

  // close Location Serch, location selection effects
  useEffect(() => {
    if (
      componentDidMount &&
      sourceLocation != null &&
      destinationLocation == null &&
      !stopOversPresentInDisplay &&
      !rentalCabs
    ) {
      // if source is filled and destination is null ->  during mount... change focus to To section
      if (!destinationLocation && showToSection) {
        focusToSection();
      }
    } else if (closeWidgetOnLocationUpdate(locationUpdateOrigin)) {
      setTimeout(closeSection, 150);
    }
    setLocationUpdateOrigin(null);
  }, [sourceLocation, destinationLocation]);

  const updateFrom = (origin: string) => (cityLocation: CityLocation) => {
    if (travelFor === RENTALS) {
      trackTemplateEvent(MOB_CABS_HR_LANDING_PICKUP_SELECT);
    } else if (travelFor === OUTSTATION_ONE_WAY) {
      trackTemplateEvent(MOB_CABS_OW_LANDING_PICKUP_SELECT);
    } else if (travelFor === OUTSTATION_ROUND_TRIP) {
      trackTemplateEvent(MOB_CABS_RT_LANDING_PICKUP_SELECT);
    } else {
      trackTemplateEvent(MOB_CABS_AT_LANDING_PICKUP_SELECT);
    }
    setLocationUpdateOrigin(origin);
    onFromChange(cityLocation);
  };

  const focusToSection = () => {
    setFromSelected(false);
    setToSelected(true);
    toInputRef?.current?.focus();
  };

  const updateTo = (origin: string) => (cityLocation: CityLocation) => {
    if (travelFor === OUTSTATION_ONE_WAY) {
      trackTemplateEvent(MOB_CABS_OW_LANDING_DESTINATION_SELECT);
    } else if (travelFor === OUTSTATION_ROUND_TRIP) {
      trackTemplateEvent(MOB_CABS_RT_LANDING_DESTINATION_SELECT);
    } else {
      trackTemplateEvent(MOB_CABS_AT_LANDING_DESTINATION_SELECT);
    }
    setLocationUpdateOrigin(origin);
    onToChange(cityLocation);
  };

  const decorateLocation = location => {
    if (!location) return null;
    const { city, subText } = location;
    if (city && subText) {
      return `${city}, ${subText}`;
    }
    if (city) {
      return city;
    }
  };

  const fromImg = `${PUBLIC_URL}/static/public/fromImg.png`;
  const backArrowImg = `${PUBLIC_URL}/static/public/backIc.png`;
  const recentImg = `${PUBLIC_URL}/static/public/recent.png`;

  const showSuggestions = suggestions && suggestions.length > 0;
  const showRecentSearches =
    !showSuggestions && recentSearches && recentSearches.length > 0;

  const showToSection =
    isAirportCheck(travelFor) ||
    (isOutstationCheck(travelFor) && activeStopOver == null);

  const showSwapSection =
    showToSection &&
    sourceLocation &&
    destinationLocation &&
    isOutstationCheck(travelFor);

  const rentalCabs = travelFor === RENTALS;

  const handleOnFromFocus = () => {
    setFromInputValue('');
    setFromSelected(true);
    setToSelected(false);
  };

  const handleOnToFocus = () => {
    setToInputValue('');
    setToSelected(true);
    setFromSelected(false);
  };

  const getLocationUpdater = origin => {
    return fromSelected ? updateFrom(origin) : updateTo(origin);
  };

  const updateFromOpacity = (opacity: number) => {
    const element = document.getElementById('floating-inputFROM');
    if (element) {
      element.style.opacity = opacity.toString();
    }
  };
  const updateToOpacity = (opacity: number) => {
    const element = document.getElementById('floating-inputTO');
    if (element) {
      element.style.opacity = opacity.toString();
    }
  };
  const onFromBlur = () => {
    setFromInputValue(null);
    updateFromOpacity(0);
  };

  const onToBlur = () => {
    setToInputValue(null);
    updateToOpacity(0);
  };

  const getFromInputValue = () => {
    if (fromInputValue == null) {
      return decorateLocation(sourceLocation);
    }
    return fromInputValue;
  };

  const getToInputValue = () => {
    if (toInputValue == null) {
      return decorateLocation(destinationLocation);
    }
    return toInputValue;
  };

  const getDecoratedText = (cityData: CityData, inputValue: string | null) => {
    try {
      if (inputValue) {
        const startIndex = cityData.title
          .toLowerCase()
          .indexOf(inputValue.toLowerCase());
        if (startIndex >= 0) {
          const len = inputValue?.length || 0;
          if (len) {
            const textToReplace = cityData.title.substring(
              startIndex,
              startIndex + len
            );
            const restOfThText = cityData.title.substring(startIndex + len);
            const highligtedSubString = `*${textToReplace}*${restOfThText}`;
            return highligtedSubString;
          }
        }
      }
    } catch (ex) {
      // console.error("exception in getDecoratedText", ex)
    }
    return cityData.title;
  };

  const getDecoratedSuggestions = () => {
    try {
      const decoratedSuggestions = suggestions?.map((cityData: CityData) => {
        const highligtedSubString = getDecoratedText(
          cityData,
          fromInputValue || toInputValue || ''
        );
        return {
          ...cityData,
          title: highligtedSubString
        };
      });
      return decoratedSuggestions || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  };
  return (
    <div className="searchPage">
      {!(stopOversPresentInDisplay && toSelected) && (
        <ReactFloatingInput
          label={fromLabel}
          imgSrc={backArrowImg}
          placeHolder={fromPlaceHolder}
          onChange={val => setFromInputValue(val)} // change on input
          value={
            fromSelected
              ? getFromInputValue()
              : decorateLocation(sourceLocation)
          }
          onFocus={handleOnFromFocus}
          onBlur={onFromBlur}
          inputRef={fromInputRef}
          onImgClick={() => {
            closeSection();
            deactivateStopOver && deactivateStopOver();
          }}
        />
      )}
      {
        <SwapIcon
          showIcon={showSwapSection && !stopOversPresentInDisplay}
          imageStyleObject={{ top: '-10px', right: '20px' }}
          styleObject={{ zIndex: 2 }}
        />
      }
      <div className="seperatorLine" />
      {showToSection && !(stopOversPresentInDisplay && fromSelected) && (
        <ReactFloatingInput
          label={toLabel}
          imgSrc={stopOversPresentInDisplay ? backArrowImg : fromImg}
          placeHolder={toPlaceHolder}
          onChange={val => setToInputValue(val)}
          onBlur={onToBlur}
          value={
            toSelected
              ? getToInputValue()
              : decorateLocation(destinationLocation)
          }
          inputRef={toInputRef}
          onFocus={handleOnToFocus}
          onImgClick={() => {
            if (stopOversPresentInDisplay) {
              closeSection();
              deactivateStopOver && deactivateStopOver();
            }
          }}
        />
      )}
      <div className="suggestContainer">
        {showSuggestions && (
          <SearchSuggestionList
            updateJourney={getLocationUpdater('SUGGESTIONS')}
            suggestions={getDecoratedSuggestions()}
            heading={null}
            imgSrc={recentImg}
          ></SearchSuggestionList>
        )}
        {showRecentSearches && (
          <SearchSuggestionList
            updateJourney={getLocationUpdater('RECENT_SEARCHES')}
            suggestions={recentSearches}
            heading={'RECENT SEARCH'}
            imgSrc={recentImg}
          ></SearchSuggestionList>
        )}
      </div>
    </div>
  );
};
export default SearchPage;

const getCityLocationFromCityData = async (cityData: CityData) => {
  const geoLocation = await getPlaceObject(cityData.placeId);
  const cityLocation: CityLocation = {
    city: cityData.title,
    subText: cityData.subTitle,
    placeId: cityData.placeId,
    geoLocation
  };

  if (cityData.cityCode) {
    cityLocation.cityCode = cityData.cityCode;
  }

  return cityLocation;
};
export const OutStationSearch = () => {
  const cabsLandingContext = useContext(CabsLandingContext);
  const dispatch = useContext(CabsLandingDispatchContext);

  const { sourceCity, destinationCity, widget, stopOvers } = cabsLandingContext;

  const stopOversPresent = stopOvers.length > 0;

  const setSourceCity = sourceCity => {
    dispatch && dispatch({ type: SET_SOURCE_CITY, newStateValue: sourceCity });
  };
  const setDestinationCity = destinationCity => {
    dispatch &&
      dispatch({ type: SET_DESTINATION_CITY, newStateValue: destinationCity });
  };

  const onSourceCityChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setSourceCity(cityLocation);
  };

  const onDestinationCityChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setDestinationCity(cityLocation);
  };

  let focusFrom = false;
  if (
    widget == cabsLandingWidgetInfo.OUTSTATION_PICKUP_LOCATION_WIDGET ||
    (!sourceCity && !stopOversPresent)
  ) {
    focusFrom = true;
  }
  return (
    <SearchPage
      focusFrom={focusFrom}
      focusTo={!focusFrom}
      sourceLocation={sourceCity}
      destinationLocation={destinationCity}
      onFromChange={onSourceCityChange}
      onToChange={onDestinationCityChange}
    />
  );
};

export const ToAirportSearch = () => {
  const cabsLandingContext = useContext(CabsLandingContext);
  const dispatch = useContext(CabsLandingDispatchContext);

  const { airport, airportCity, widget } = cabsLandingContext;
  const setAirportCity = airportCity => {
    dispatch &&
      dispatch({ type: SET_AIRPORT_CITY, newStateValue: airportCity });
  };
  const setAirport = airport => {
    dispatch && dispatch({ type: SET_AIRPORT, newStateValue: airport });
  };

  const onAirportCityChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setAirportCity(cityLocation);
  };

  const onAirportChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setAirport(cityLocation);
  };

  let focusFrom = false;
  if (
    widget == cabsLandingWidgetInfo.TO_AIRPORT_CITY_LOCATION_WIDGET ||
    !airportCity
  ) {
    focusFrom = true;
  }

  return (
    <SearchPage
      focusFrom={focusFrom}
      focusTo={!focusFrom}
      fromLabel="FROM LOCATION"
      toLabel="TO AIRPORT"
      sourceLocation={airportCity}
      destinationLocation={airport}
      onFromChange={onAirportCityChange}
      onToChange={onAirportChange}
    />
  );
};

export const FromAirportSearch = () => {
  const cabsLandingContext = useContext(CabsLandingContext);
  const dispatch = useContext(CabsLandingDispatchContext);

  const { airport, airportCity, widget } = cabsLandingContext;

  const setAirportCity = airportCity => {
    dispatch &&
      dispatch({ type: SET_AIRPORT_CITY, newStateValue: airportCity });
  };

  const setAirport = airport => {
    dispatch && dispatch({ type: SET_AIRPORT, newStateValue: airport });
  };

  const onAirportCityChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setAirportCity(cityLocation);
  };

  const onAirportChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setAirport(cityLocation);
  };

  let focusFrom = false;
  if (
    widget == cabsLandingWidgetInfo.FROM_AIRPORT_LOCATION_WIDGET ||
    !airport
  ) {
    focusFrom = true;
  }

  return (
    <SearchPage
      focusFrom={focusFrom}
      focusTo={!focusFrom}
      fromLabel="FROM AIRPORT"
      toLabel="TO LOCATION"
      sourceLocation={airport}
      destinationLocation={airportCity}
      onFromChange={onAirportChange}
      onToChange={onAirportCityChange}
    />
  );
};

export const RentalSearch = () => {
  const cabsLandingContext = useContext(CabsLandingContext);
  const dispatch = useContext(CabsLandingDispatchContext);

  const { rentalCity } = cabsLandingContext;
  const setRentalCity = airportCity => {
    dispatch && dispatch({ type: SET_RENTAL_CITY, newStateValue: airportCity });
  };

  const onRentalCityChange = async (cityData: CityData) => {
    const cityLocation = await getCityLocationFromCityData(cityData);
    setRentalCity(cityLocation);
  };

  return (
    <SearchPage
      sourceLocation={rentalCity}
      destinationLocation={null}
      onFromChange={onRentalCityChange}
      onToChange={null}
    />
  );
};

export const StopOverSearch = () => {
  const cabsLandingContext = useContext(CabsLandingContext);
  const dispatch = useContext(CabsLandingDispatchContext);

  const { stopOvers, activeStopOver } = cabsLandingContext;

  const setStopOvers = stopOvers => {
    dispatch && dispatch({ type: SET_STOPOVERS, newStateValue: stopOvers });
  };

  const getActiveStopOverCity = () => {
    const stopOverCity = stopOvers.filter((_, idx) => {
      return activeStopOver == idx;
    });
    return stopOverCity;
  };

  const unsetActiveStopOver = () => {
    dispatch && dispatch({ type: SET_ACTIVE_STOPOVER, newStateValue: null });
  };

  const onStopOverUpdate = async cityData => {
    const newCityLocation = await getCityLocationFromCityData(cityData);
    const newStopOvers = stopOvers.map((cityLocation, idx) => {
      if (activeStopOver == idx) {
        return newCityLocation;
      }
      return cityLocation;
    });
    setStopOvers(newStopOvers);
    unsetActiveStopOver();
  };
  return (
    <SearchPage
      fromLabel={`STOP ${Number(activeStopOver) + 1}`}
      fromPlaceHolder={`Enter location for Stop ${Number(activeStopOver) + 1}`}
      sourceLocation={getActiveStopOverCity()}
      destinationLocation={null}
      onFromChange={onStopOverUpdate}
      onToChange={null}
      deactivateStopOver={unsetActiveStopOver}
    />
  );
};
